import { Box, Paper, useMediaQuery, useTheme, Drawer, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslate, useStore } from 'react-admin';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTicketsPage } from './hooks/useTicketsPage';
import { TicketsManager } from './components/TicketsManager';
import { ChatPanelContainer } from './components/ChatPanel';
import { DetailsPanel } from './components/DetailsPanel';
import { ConnectionStatusBanner } from '../../components/chat/ConnectionStatusBanner';
import { EmptyConversationState } from '../../components/chat/EmptyConversationState';
import type { Ticket } from '../../types/chat.types';

/**
 * TicketsPage - Ticketz/WhatIcket-style ticket management interface
 *
 * Layout based on ticketz/whaticket:
 * - Left Panel (25-33%): TicketsManager with tabs (Open/Closed/Search)
 * - Center Panel (flexible): Chat conversation with WhatsApp-style background
 * - Right Panel (300px, when open): Details panel fixed on the right
 *
 * Features:
 * - WhatsApp-style background (light/dark mode aware)
 * - 3-column flex layout (tickets list + chat + details)
 * - Responsive design with mobile drawer navigation
 * - URL-based ticket selection
 * - Real-time message updates
 *
 * @example
 * ```tsx
 * // In src/resources/index.ts
 * {
 *   name: "tickets",
 *   list: TicketsPage,
 *   icon: TicketsIcon,
 * }
 * ```
 */
export function TicketsPage() {
  const theme = useTheme();
  const translate = useTranslate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useStore('sidebar.open', true);
  const previousSidebarState = useRef(sidebarOpen);

  // Collapse platform sidebar on mount, restore on unmount
  useEffect(() => {
    previousSidebarState.current = sidebarOpen;
    setSidebarOpen(false);
    return () => {
      setSidebarOpen(previousSidebarState.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    selectedTicketId,
    selectedTicket,
    selectedConversation: rawConversation,
    isLoadingConversation,
    isDetailsPanelOpen,
    isConversationListOpen,
    selectTicket,
    toggleDetailsPanel,
    toggleConversationList,
    closeDetailsPanel,
  } = useTicketsPage();

  // Enrich conversation with the selected ticket
  const selectedConversation = rawConversation && selectedTicket
    ? {
        ...rawConversation,
        tickets: rawConversation.tickets?.length
          ? rawConversation.tickets
          : [selectedTicket],
      }
    : rawConversation;

  // Handle ticket selection from TicketsManager
  const navigate = useNavigate();

  const handleTicketSelect = (ticket: Ticket) => {
    selectTicket(Number(ticket.id));
  };

  const handleForwardSuccess = useCallback((conversationId: number) => {
    navigate(`/conversations?conversation=${conversationId}`);
  }, [navigate]);

  // Mobile layout: drawer for tickets list
  if (isMobile) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 64px)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Connection status banner */}
        <ConnectionStatusBanner position="top" />

        {/* Floating menu button to open tickets list */}
        {!isConversationListOpen && (
          <IconButton
            onClick={toggleConversationList}
            sx={{
              position: 'fixed',
              top: 80,
              left: 16,
              zIndex: 1200,
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              boxShadow: 2,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Left Drawer: Tickets Manager */}
        <Drawer
          anchor="left"
          open={isConversationListOpen}
          onClose={toggleConversationList}
          sx={{
            '& .MuiDrawer-paper': {
              width: '85%',
              maxWidth: 360,
              mt: '64px',
            },
          }}
        >
          <TicketsManager
            onTicketSelect={(ticket) => {
              handleTicketSelect(ticket);
              toggleConversationList();
            }}
            selectedTicketId={selectedTicketId}
          />
        </Drawer>

        {/* Main: Chat Panel with WhatsApp Background */}
        <Box
          sx={{
            height: '100%',
          }}
        >
          <ChatPanelContainer
            conversation={selectedConversation}
            isLoading={isLoadingConversation}
            onToggleDetails={toggleDetailsPanel}
            isDetailsOpen={isDetailsPanelOpen}
            onForwardSuccess={handleForwardSuccess}
          />
        </Box>

        {/* Right Drawer: Details Panel */}
        <Drawer
          anchor="right"
          open={isDetailsPanelOpen && !!selectedConversation}
          onClose={closeDetailsPanel}
          sx={{
            '& .MuiDrawer-paper': {
              width: '85%',
              maxWidth: 320,
              mt: '64px',
            },
          }}
        >
          <DetailsPanel
            conversation={selectedConversation}
            ticket={selectedTicket}
            onClose={closeDetailsPanel}
            showCloseButton={true}
          />
        </Drawer>
      </Box>
    );
  }

  // Desktop/Tablet layout: 2-column flex + details drawer overlay
  return (
    <Box
      sx={{
        height: 'calc(100vh - 48px)',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Connection status banner */}
      <ConnectionStatusBanner position="top" />

      <Paper
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left Panel: Tickets Manager */}
        <Box
          sx={{
            width: '30%',
            minWidth: 300,
            maxWidth: 400,
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
            overflowY: 'hidden',
          }}
        >
          <TicketsManager
            onTicketSelect={handleTicketSelect}
            selectedTicketId={selectedTicketId}
          />
        </Box>

        {/* Center Panel: Chat (always full remaining width) */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          {selectedTicketId ? (
            <ChatPanelContainer
              conversation={selectedConversation}
              isLoading={isLoadingConversation}
              onToggleDetails={toggleDetailsPanel}
              isDetailsOpen={isDetailsPanelOpen}
              onForwardSuccess={handleForwardSuccess}
            />
          ) : (
            <Paper
              square
              variant="outlined"
              sx={{
                display: 'flex',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <EmptyConversationState
                title={translate('resources.chat.noTicketSelected')}
                subtitle={translate('resources.chat.selectTicketToView')}
              />
            </Paper>
          )}
        </Box>
      </Paper>

      {/* Details Drawer: overlay on top of chat */}
      <Drawer
        anchor="right"
        open={isDetailsPanelOpen && !!selectedConversation}
        onClose={closeDetailsPanel}
        sx={{
          '& .MuiDrawer-paper': {
            width: 380,
            maxWidth: '90vw',
            mt: '48px',
            height: 'calc(100vh - 48px)',
          },
        }}
      >
        <DetailsPanel
          conversation={selectedConversation}
          ticket={selectedTicket}
          onClose={closeDetailsPanel}
          showCloseButton={true}
        />
      </Drawer>
    </Box>
  );
}

export default TicketsPage;
